'use client';

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const MATURITIES: { id: string; label: string; order: number }[] = [
  { id: 'DGS3MO', label: '3M', order: 0 },
  { id: 'DGS2',   label: '2Y', order: 1 },
  { id: 'DGS5',   label: '5Y', order: 2 },
  { id: 'DGS10', label: '10Y', order: 3 },
  { id: 'DGS30', label: '30Y', order: 4 },
];

interface FredPayload {
  observations: { date: string; value: number | null }[];
  lastUpdated: string;
}

export function YieldCurveSnapshot({ height = 220 }: { height?: number }) {
  const queries = useQueries({
    queries: MATURITIES.map((m) => ({
      queryKey: ['/api/terminal/fred', m.id, '1Y'],
      queryFn: async () => {
        const res = await fetch(`/api/terminal/fred/${m.id}?range=1Y`);
        if (!res.ok) throw new Error('Failed to fetch maturity');
        return res.json() as Promise<FredPayload>;
      },
      staleTime: 12 * 60 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  const queryData = queries.map((q) => q.data);
  const data = useMemo(() => {
    const targetMs = Date.now() - 365 * 24 * 60 * 60 * 1000;
    return MATURITIES.map((m, i) => {
      const obs = queryData[i]?.observations ?? [];
      const valid = obs.filter((o) => o.value !== null) as { date: string; value: number }[];
      const today = valid.length ? valid[valid.length - 1].value : null;
      // Pick the observation closest to ~1 year ago (handles gaps/holidays).
      let yearAgo: number | null = null;
      let bestDelta = Infinity;
      for (const o of valid) {
        const delta = Math.abs(Date.parse(o.date) - targetMs);
        if (delta < bestDelta) {
          bestDelta = delta;
          yearAgo = o.value;
        }
      }
      return { maturity: m.label, today, yearAgo, order: m.order };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, queryData);

  return (
    <div className="bg-[#111827] border border-[#1F2937] p-4">
      <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-1">
        Yield Curve Snapshot
      </div>
      <div className="text-[11px] text-[#6B7280] mb-3">
        Treasury yields by maturity, today vs ~1 year ago. Shape inversions are a watched recession signal.
      </div>

      {isLoading ? (
        <div className="w-full bg-[#1F2937] animate-pulse rounded" style={{ height }} />
      ) : isError ? (
        <div className="text-xs text-[#6B7280] py-8 text-center">Data unavailable</div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={data} margin={{ top: 4, right: 16, bottom: 0, left: 4 }}>
            <XAxis
              dataKey="maturity"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 11, fontFamily: 'JetBrains Mono' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              tickFormatter={(v) => v.toFixed(2) + '%'}
              domain={['dataMin - 0.2', 'dataMax + 0.2']}
              width={55}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#1A2035', border: '1px solid #1F2937', borderRadius: '2px', fontSize: '12px', fontFamily: 'JetBrains Mono', color: '#E5E7EB' }}
              formatter={(value: number | undefined, name) => [(value?.toFixed(2) ?? '0') + '%', name]}
            />
            <Legend wrapperStyle={{ fontSize: '11px', fontFamily: 'JetBrains Mono', color: '#6B7280' }} />
            <Line type="monotone" dataKey="today"   name="Today"   stroke="var(--color-accent)" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="yearAgo" name="1Y ago"  stroke="#6B7280"             strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
